import type { SituationChoice } from "@/pages/onboarding/OnboardingSituationStep";

/**
 * Ordering helpers for the certificate-vs-DGI phase of self onboarding.
 *
 * The two situations that acquire a *fresh* certificate — `scratch` (brand-new
 * emisor) and `issuer_need_regularization` (registered emisor renewing its cert)
 * — share the "soft cert" semantics (the cert may not be in hand at CAE, so the
 * user can advance past it; the DGI sub-flow shows a 4-row trail; the Abitab step
 * advances as soon as the cert request is in progress). But they differ on
 * ORDER:
 *
 *   - `scratch` is DGI-first (DGI -> cert -> CAE): a brand-new emisor has no
 *     DGI-authorized identity to sign a certificate for until DGI authorizes it.
 *   - `issuer_need_regularization` is cert-first (cert -> DGI -> CAE): the emisor
 *     is already registered, so the cert can be requested up front.
 *
 * A single boolean cannot express both, so these are split:
 * `isNewCertFlow` = "a fresh cert is being acquired" (soft-cert semantics),
 * `isCertBeforeDgi` = "the cert is ordered before DGI" (regularization only).
 */

/** True when a fresh certificate is being acquired (scratch or regularization). */
export function isNewCertFlow(situationChoice: SituationChoice | null): boolean {
  return (
    situationChoice === "scratch" ||
    situationChoice === "issuer_need_regularization"
  );
}

/**
 * True when the certificate phase is ordered *before* the DGI portal-user step.
 * Regularization only — `scratch` is DGI-first.
 */
export function isCertBeforeDgi(
  situationChoice: SituationChoice | null,
): boolean {
  return situationChoice === "issuer_need_regularization";
}
